import { audio } from '../../../../audio/AudioManager.js';
import { SaveManager } from '../../../../state/SaveManager.js';
import { applicationLifecycle } from '../../../shared/lifecycle/index.js';
import { ensureMinimumTextTouchTarget } from '../../../ui/TouchTarget.js';
import {
  calculateDiscoveryLabSafeBounds,
  isCompactDiscoveryLabLayout,
} from '../components/DiscoveryLabLayout.js';
import { DISCOVERY_LAB_ASSETS } from '../DiscoveryLabAssets.js';
import { DISCOVERY_LAB_DISTRICTS, DISCOVERY_LAB_LAYOUT } from '../DiscoveryLabConfig.js';
import {
  completeDiscoveryLabDistrictProgress,
  getDiscoveryLabState,
  saveDiscoveryLabDistrictProgress,
  visitDiscoveryLabDistrict,
} from '../DiscoveryLabState.js';
import {
  DISCOVERY_OBSERVATORY_ASSETS,
  DISCOVERY_OBSERVATORY_ASSET_LIST,
  canEnterDiscoveryObservatory,
  getMissingDiscoveryObservatoryAssets,
} from './DiscoveryObservatoryAssets.js';
import {
  DISCOVERY_OBSERVATORY_RESTORATION_EVENT,
  DISCOVERY_OBSERVATORY_TRIALS,
} from './DiscoveryObservatoryConfig.js';
import {
  DiscoveryObservatoryState,
  type DiscoveryObservatorySerializedState,
} from './DiscoveryObservatoryState.js';

export const DISCOVERY_OBSERVATORY_SCENE_KEY = 'DiscoveryObservatory';
const FONT_UI = '"Nunito", "Segoe UI", Arial, sans-serif';

interface ObservatoryProgress extends DiscoveryObservatorySerializedState {
  readonly restored: boolean;
}

interface ChoiceView {
  readonly image: Phaser.GameObjects.Image;
  readonly label: Phaser.GameObjects.Text;
  readonly badge: Phaser.GameObjects.Text;
}

interface ArtButton {
  readonly image: Phaser.GameObjects.Image;
  readonly label: Phaser.GameObjects.Text;
}

export class DiscoveryObservatoryScene extends Phaser.Scene {
  static readonly requiredImageKeys = DISCOVERY_OBSERVATORY_ASSET_LIST.map(asset => asset.key);

  private state!: DiscoveryObservatoryState;
  private dormant!: Phaser.GameObjects.Image;
  private restoredBackground!: Phaser.GameObjects.Image;
  private panel!: Phaser.GameObjects.Image;
  private cosmo!: Phaser.GameObjects.Image;
  private title!: Phaser.GameObjects.Text;
  private focus!: Phaser.GameObjects.Text;
  private evidenceRecord!: Phaser.GameObjects.Text;
  private prompt!: Phaser.GameObjects.Text;
  private feedback!: Phaser.GameObjects.Text;
  private controls!: Phaser.GameObjects.Text;
  private back!: Phaser.GameObjects.Text;
  private home!: Phaser.GameObjects.Text;
  private finale!: Phaser.GameObjects.Text;
  private choices: ChoiceView[] = [];
  private lenses: Phaser.GameObjects.Image[] = [];
  private lensLabels: Phaser.GameObjects.Text[] = [];
  private actions: ArtButton[] = [];
  private isRestored = false;
  private leaving = false;
  private suspended = false;
  private reducedMotion = false;
  private renderScale = 1;
  private resizeTimer?: number;
  private unsubscribeLifecycle?: () => void;

  constructor() {
    super({ key: DISCOVERY_OBSERVATORY_SCENE_KEY });
  }

  create(): void {
    this.choices = [];
    this.lenses = [];
    this.lensLabels = [];
    this.actions = [];
    this.leaving = false;
    this.suspended = false;

    const world = getDiscoveryLabState();
    const missing = getMissingDiscoveryObservatoryAssets(this.textures);
    if (
      !world.unlockedDistricts.includes(DISCOVERY_LAB_DISTRICTS.discoveryObservatory)
      || !canEnterDiscoveryObservatory(this.textures)
    ) {
      if (missing.length > 0) {
        console.error(
          '[DiscoveryObservatory] Required production textures are missing:',
          missing.map(asset => `${asset.key} (${asset.path})`),
        );
      }
      this.scene.start('EarthMaterialsYard');
      return;
    }

    void audio.enterSoundscape('discovery-lab');
    this.renderScale = this.configureRenderer();
    this.reducedMotion = SaveManager.getSettings().reducedMotion
      || (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
    this.isRestored = world.completedDistricts.includes(
      DISCOVERY_LAB_DISTRICTS.discoveryObservatory,
    );
    const saved = world.districtProgress[
      DISCOVERY_LAB_DISTRICTS.discoveryObservatory
    ] as Partial<ObservatoryProgress> | undefined;
    this.state = new DiscoveryObservatoryState(
      DISCOVERY_OBSERVATORY_TRIALS,
      saved?.version === 1 ? saved : undefined,
    );
    if (saved?.version === 1) this.isRestored = saved.restored === true;
    visitDiscoveryLabDistrict(DISCOVERY_LAB_DISTRICTS.discoveryObservatory);
    SaveManager.setLastScene(DISCOVERY_OBSERVATORY_SCENE_KEY);
    SaveManager.save();
    this.build();
    this.registerInput();
    this.registerLifecycle();
    this.refresh();
    this.applyTextResolution();
    this.cameras.main.fadeIn(this.reducedMotion ? 150 : 520, 18, 90, 95);
  }

  private build(): void {
    this.dormant = this.add.image(
      640,
      360,
      DISCOVERY_OBSERVATORY_ASSETS.dormant.key,
    ).setDisplaySize(1280, 720).setDepth(0);
    this.restoredBackground = this.add.image(
      640,
      360,
      DISCOVERY_OBSERVATORY_ASSETS.restored.key,
    ).setDisplaySize(1280, 720).setDepth(1);
    this.panel = this.add.image(640, 104, DISCOVERY_LAB_ASSETS.labMissionPanel.key).setDepth(30);
    this.cosmo = this.add.image(100, 650, DISCOVERY_LAB_ASSETS.cosmo.key).setDepth(105);
    this.title = this.text('DISCOVERY OBSERVATORY', 28, '#ffffff')
      .setStroke('#063e49', 7).setDepth(110);
    this.focus = this.text('', 11, '#d7fbff').setStroke('#063e49', 4).setDepth(111);
    this.evidenceRecord = this.text('', 14, '#ffffff')
      .setStroke('#063e49', 4).setWordWrapWidth(760).setDepth(42);
    this.prompt = this.text('', 14, '#ffffff')
      .setStroke('#063e49', 5).setDepth(110);
    this.feedback = this.text('', 12, '#ffffff')
      .setStroke('#063e49', 5).setWordWrapWidth(790).setDepth(110);
    this.controls = this.text(
      '1–3 choose • Enter check/continue • H hint • R reset • Esc Earth Yard • M Home',
      9,
      '#ffffff',
    ).setStroke('#063e49', 5).setDepth(110);
    this.back = this.text('←  EARTH MATERIALS YARD', 12, '#ffffff')
      .setStroke('#063e49', 6).setInteractive({ useHandCursor: true }).setDepth(112);
    this.home = this.text('HOME HUB  (M)', 13, '#ffffff')
      .setStroke('#063e49', 6).setInteractive({ useHandCursor: true }).setDepth(112);
    this.finale = this.text('DISCOVERY LAB RESTORED • RETURN HOME', 14, '#d7fbff')
      .setStroke('#063e49', 6).setInteractive({ useHandCursor: true }).setDepth(112);
    [this.back, this.home, this.finale].forEach(target => ensureMinimumTextTouchTarget(target));
    this.back.on('pointerup', this.returnToYard, this);
    this.home.on('pointerup', this.returnHome, this);
    this.finale.on('pointerup', this.returnHome, this);
    for (let index = 0; index < 3; index += 1) {
      const view = this.choiceView();
      view.image.on('pointerup', () => this.choose(index));
      this.choices.push(view);
    }
    for (let index = 0; index < 6; index += 1) {
      this.lenses.push(this.add.image(
        0,
        0,
        DISCOVERY_OBSERVATORY_ASSETS.masteryLens.key,
      ).setDepth(20));
      this.lensLabels.push(this.text(`${index + 1}`, 10, '#ffffff')
        .setStroke('#063e49', 4).setDepth(22));
    }
    this.actions = [
      this.button('CHECK', () => this.check()),
      this.button('HINT', () => this.hint()),
      this.button('RESET', () => this.reset()),
    ];
  }

  private text(value: string, size: number, color: string): Phaser.GameObjects.Text {
    return this.add.text(0, 0, value, {
      fontFamily: FONT_UI,
      fontSize: `${size}px`,
      fontStyle: 'bold',
      color,
      align: 'center',
      lineSpacing: 3,
    }).setOrigin(0.5);
  }

  private choiceView(): ChoiceView {
    const image = this.add.image(0, 0, DISCOVERY_OBSERVATORY_ASSETS.masteryLens.key)
      .setDepth(60).setInteractive({ useHandCursor: true });
    const label = this.text('', 11, '#16464b').setWordWrapWidth(150).setDepth(62);
    const badge = this.text('', 11, '#ffffff').setStroke('#063e49', 4).setDepth(63);
    return { image, label, badge };
  }

  private button(label: string, action: () => void): ArtButton {
    const image = this.add.image(0, 0, DISCOVERY_OBSERVATORY_ASSETS.masteryLens.key)
      .setDepth(100).setInteractive({ useHandCursor: true });
    const text = this.text(label, 10, '#16464b').setDepth(101);
    image.on('pointerup', action);
    return { image, label: text };
  }

  private choose(index: number): void {
    if (!this.canAct()) return;
    const choice = this.state.trial.choices[index];
    if (!choice || !this.state.choose(choice.id)) return;
    this.feedback.setText('Evidence claim selected. You can revise it before checking.');
    audio.click();
    this.capture();
  }

  private check(): void {
    if (this.isRestored) return this.replay();
    if (!this.canAct()) return;
    if (this.state.snapshot.complete && this.state.hasNextTrial) {
      this.state.nextTrial();
      this.feedback.setText('The next science lens is ready.');
      audio.click();
      return this.capture();
    }
    const result = this.state.check();
    if (result === 'incomplete') {
      this.feedback.setText('Choose the claim supported by the evidence record before checking.');
      audio.wrong();
      return this.capture();
    }
    if (result === 'incorrect') {
      SaveManager.recordAnswer(this.state.trial.skillId, false);
      this.feedback.setText(
        'That claim does not fit the full evidence record. Use the evidence and revise your choice.',
      );
      audio.wrong();
      return this.capture();
    }
    SaveManager.recordAnswer(this.state.trial.skillId, true);
    this.feedback.setText(this.state.trial.explanation);
    if (this.state.mastered) {
      this.isRestored = true;
      completeDiscoveryLabDistrictProgress(
        DISCOVERY_LAB_DISTRICTS.discoveryObservatory,
        this.progress(),
        DISCOVERY_OBSERVATORY_RESTORATION_EVENT,
      );
      this.magic();
      audio.restoration();
    } else audio.correct();
    this.capture();
  }

  private hint(): void {
    if (!this.canAct()) return;
    this.feedback.setText(this.state.requestHint());
    audio.hint();
    this.capture();
  }

  private reset(): void {
    if (!this.canAct() || !this.state.resetCurrent()) return;
    this.feedback.setText('The current choice clears. Every completed science lens stays safe.');
    audio.click();
    this.capture();
  }

  private replay(): void {
    this.isRestored = false;
    this.state.replay();
    this.feedback.setText('Replay mode: relight the six science lenses without changing saved mastery.');
    audio.click();
    this.capture();
  }

  private capture(): void {
    saveDiscoveryLabDistrictProgress(
      DISCOVERY_LAB_DISTRICTS.discoveryObservatory,
      this.progress(),
    );
    this.refresh();
  }

  private progress(): ObservatoryProgress {
    return { ...this.state.serialize(), restored: this.isRestored };
  }

  private refresh(): void {
    const trial = this.state.trial;
    const current = this.state.snapshot;
    this.dormant.setAlpha(this.isRestored ? 0 : 1);
    this.restoredBackground.setAlpha(this.isRestored ? 1 : 0);
    this.focus.setText(
      this.isRestored
        ? 'ALL SIX SCIENCE LENSES RESTORED'
        : `${trial.focus.toLocaleUpperCase().replace('-', ' ')} • LENS ${DISCOVERY_OBSERVATORY_TRIALS.indexOf(trial) + 1} OF 6`,
    );
    this.evidenceRecord.setText(
      this.isRestored
        ? 'Every restored field station now sends its discoveries to the glowing Observatory archive.'
        : trial.evidenceRecord,
    );
    this.prompt.setText(this.isRestored ? '' : trial.prompt);
    this.choices.forEach((view, index) => {
      const choice = trial.choices[index];
      const visible = !this.isRestored;
      const selected = current.selectedChoiceId === choice.id;
      view.image.setVisible(visible).setAlpha(selected ? 1 : 0.78).setScale(selected ? 1.04 : 1);
      view.label.setVisible(visible).setText(choice.text);
      view.badge.setVisible(visible).setText(`${index + 1}`);
    });
    const complete = new Set(this.state.completedTrialIds);
    this.lenses.forEach((lens, index) => {
      const lit = complete.has(DISCOVERY_OBSERVATORY_TRIALS[index].id) || this.isRestored;
      lens.setAlpha(lit ? 0.9 : 0.12);
      this.lensLabels[index].setAlpha(lit ? 1 : 0.35);
    });
    this.actions.forEach((button, index) => {
      const visible = !this.isRestored || index === 0;
      button.image.setVisible(visible);
      button.label.setVisible(visible);
    });
    this.actions[0].label.setText(
      this.isRestored ? 'REPLAY' : current.complete && this.state.hasNextTrial ? 'NEXT LENS' : 'CHECK',
    );
    this.prompt.setVisible(!this.isRestored);
    this.controls.setVisible(!this.isRestored);
    this.finale.setVisible(this.isRestored);
    if (!this.feedback.text) {
      this.feedback.setText('Read the evidence record, then choose the one claim it supports.');
    }
    this.layout();
  }

  private layout(): void {
    const safe = calculateDiscoveryLabSafeBounds(this);
    const compact = isCompactDiscoveryLabLayout(this, safe);
    const portrait = safe.width < 700;
    const panelWidth = portrait
      ? safe.width - 24
      : Math.min(compact ? 760 : 880, safe.width - 190);
    this.panel.setPosition(safe.centerX, safe.y + (portrait ? 112 : compact ? 93 : 103))
      .setDisplaySize(panelWidth, panelWidth * (240 / 720));
    this.title.setPosition(safe.centerX, safe.y + (portrait ? 42 : 27))
      .setFontSize(portrait ? 19 : compact ? 23 : 28);
    this.focus.setPosition(safe.centerX, safe.y + (portrait ? 69 : 54))
      .setFontSize(portrait ? 8 : 10);
    this.evidenceRecord.setPosition(safe.centerX, safe.y + (portrait ? 116 : compact ? 99 : 111))
      .setFontSize(portrait ? 9 : compact ? 11 : 13)
      .setWordWrapWidth(panelWidth * 0.77);
    this.prompt.setPosition(safe.centerX, safe.y + (portrait ? 174 : compact ? 164 : 180))
      .setFontSize(portrait ? 10 : compact ? 12 : 14);
    this.back.setPosition(safe.x + (portrait ? 76 : 99), safe.y + 23)
      .setFontSize(portrait ? 7 : compact ? 9 : 12);
    this.home.setPosition(safe.right - (portrait ? 52 : 66), safe.y + 23)
      .setFontSize(portrait ? 8 : compact ? 10 : 13);

    const choiceGap = portrait ? Math.min(188, (safe.width - 40) / 3) : compact ? 215 : 242;
    const choiceSize = portrait ? 166 : compact ? 188 : 208;
    const choiceY = safe.y + (portrait ? 300 : compact ? 294 : 322);
    this.choices.forEach((view, index) => {
      const x = safe.centerX + (index - 1) * choiceGap;
      view.image.setPosition(x, choiceY).setDisplaySize(choiceSize, choiceSize);
      view.label.setPosition(x, choiceY).setFontSize(portrait ? 8 : compact ? 9 : 11)
        .setWordWrapWidth(choiceSize * 0.61);
      view.badge.setPosition(x, choiceY - choiceSize * 0.36)
        .setFontSize(portrait ? 8 : 10);
    });

    const markerPositions = portrait
      ? [[0.17, 0.24], [0.11, 0.39], [0.15, 0.53], [0.83, 0.24], [0.89, 0.39], [0.85, 0.53]]
      : [[0.34, 0.18], [0.24, 0.30], [0.23, 0.47], [0.66, 0.18], [0.76, 0.30], [0.77, 0.47]];
    this.lenses.forEach((lens, index) => {
      const [normalX, normalY] = markerPositions[index];
      const x = safe.x + safe.width * normalX;
      const y = safe.y + safe.height * normalY;
      const size = portrait ? 46 : compact ? 52 : 60;
      lens.setPosition(x, y).setDisplaySize(size, size);
      this.lensLabels[index].setPosition(x, y).setFontSize(portrait ? 7 : 9);
    });

    const actionY = safe.bottom - (portrait ? 49 : 43);
    this.actions.forEach((button, index) => {
      const x = safe.centerX + (index - 1) * (portrait ? 138 : 170);
      const size = portrait ? 53 : 62;
      button.image.setPosition(x, actionY).setDisplaySize(size * 2.1, size);
      button.label.setPosition(x, actionY).setFontSize(portrait ? 8 : 10);
    });
    this.feedback.setPosition(
      safe.centerX,
      safe.bottom - (this.isRestored ? (portrait ? 154 : 142) : (portrait ? 101 : 94)),
    )
      .setFontSize(portrait ? 8 : compact ? 10 : 12)
      .setWordWrapWidth(portrait ? safe.width - 30 : Math.min(790, safe.width - 170));
    this.controls.setPosition(safe.centerX, safe.bottom - 10)
      .setFontSize(portrait ? 6 : compact ? 8 : 9).setWordWrapWidth(safe.width - 20);
    this.finale.setPosition(safe.centerX, safe.bottom - (portrait ? 114 : 92))
      .setFontSize(portrait ? 10 : 14);
    this.cosmo.setPosition(
      safe.x + (portrait ? 28 : compact ? 54 : 70),
      safe.bottom - (portrait ? 42 : compact ? 50 : 60),
    ).setDisplaySize(
      portrait ? 41 : compact ? 62 : 76,
      portrait ? 55 : compact ? 82 : 101,
    );
  }

  private magic(): void {
    const effect = this.add.image(640, 390, DISCOVERY_LAB_ASSETS.restorationVfx.key)
      .setDisplaySize(620, 480).setDepth(95).setBlendMode(Phaser.BlendModes.ADD)
      .setAlpha(this.reducedMotion ? 0.72 : 0);
    if (this.reducedMotion) this.time.delayedCall(900, () => effect.destroy());
    else {
      this.tweens.add({
        targets: effect,
        alpha: 0.94,
        scaleX: 1.16,
        scaleY: 1.16,
        duration: 680,
        yoyo: true,
        hold: 200,
        onComplete: () => effect.destroy(),
      });
    }
  }

  private registerInput(): void {
    this.input.keyboard?.on('keydown', this.handleKey, this);
  }

  private handleKey(event: KeyboardEvent): void {
    if (this.leaving || this.suspended) return;
    const key = event.key.toLowerCase();
    if (key === 'escape') return this.returnToYard();
    if (key === 'm') return this.returnHome();
    if (key === 'enter' || key === ' ') return this.check();
    const index = ['1', '2', '3'].indexOf(key);
    if (index >= 0) return this.choose(index);
    if (key === 'h') return this.hint();
    if (key === 'r') this.reset();
  }

  private registerLifecycle(): void {
    this.unsubscribeLifecycle = applicationLifecycle.subscribe({
      onSuspend: () => { this.suspended = true; },
      onResume: () => { this.suspended = false; },
    });
    window.addEventListener('resize', this.handleResize);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);
  }

  private canAct(): boolean {
    return !this.leaving && !this.suspended && !this.isRestored;
  }

  private configureRenderer(): number {
    const width = this.scale.parentSize.width || window.innerWidth;
    const height = this.scale.parentSize.height || window.innerHeight;
    const scale = Phaser.Math.Clamp(
      Math.max(
        width / DISCOVERY_LAB_LAYOUT.width,
        height / DISCOVERY_LAB_LAYOUT.height,
      ) * Phaser.Math.Clamp(window.devicePixelRatio || 1, 1, 2),
      1,
      2,
    );
    this.scale.scaleMode = Phaser.Scale.ENVELOP;
    this.scale.autoCenter = Phaser.Scale.NO_CENTER;
    this.scale.displaySize.setAspectMode(Phaser.Scale.ENVELOP);
    this.scale.setGameSize(
      Math.round(DISCOVERY_LAB_LAYOUT.width * scale),
      Math.round(DISCOVERY_LAB_LAYOUT.height * scale),
    );
    this.scale.refresh();
    this.game.canvas.style.marginLeft = '0px';
    this.game.canvas.style.marginTop = '0px';
    this.cameras.main.setZoom(scale).centerOn(640, 360);
    return scale;
  }

  private applyTextResolution(): void {
    this.children.list.forEach(object => {
      if (object instanceof Phaser.GameObjects.Text) object.setResolution(this.renderScale);
    });
  }

  private readonly handleResize = (): void => {
    if (this.resizeTimer !== undefined) clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(() => {
      if (!this.scene.isActive()) return;
      this.renderScale = this.configureRenderer();
      this.layout();
      this.applyTextResolution();
    }, 80);
  };

  private readonly returnToYard = (): void => {
    if (this.leaving) return;
    this.leaving = true;
    saveDiscoveryLabDistrictProgress(
      DISCOVERY_LAB_DISTRICTS.discoveryObservatory,
      this.progress(),
    );
    this.scene.start('EarthMaterialsYard');
  };

  private readonly returnHome = (): void => {
    if (this.leaving) return;
    this.leaving = true;
    saveDiscoveryLabDistrictProgress(
      DISCOVERY_LAB_DISTRICTS.discoveryObservatory,
      this.progress(),
    );
    SaveManager.setLastScene(DISCOVERY_OBSERVATORY_SCENE_KEY);
    SaveManager.save();
    this.scene.start('HomeHub');
  };

  private shutdown(): void {
    if (this.state) {
      saveDiscoveryLabDistrictProgress(
        DISCOVERY_LAB_DISTRICTS.discoveryObservatory,
        this.progress(),
      );
    }
    this.unsubscribeLifecycle?.();
    audio.leaveSoundscape({ id: 'discovery-lab' });
    window.removeEventListener('resize', this.handleResize);
    if (this.resizeTimer !== undefined) clearTimeout(this.resizeTimer);
    this.input.keyboard?.off('keydown', this.handleKey, this);
    this.tweens.killAll();
  }
}
